/**
 * Shared Intercom Manager
 * Can be imported by both main app and ILS
 * Single source of truth for all Intercom operations
 */

class IntercomManager {
  constructor(config = {}) {
    this.isInitialized = false;
    this.config = {
      // Auto-shutdown selectors - add any logout elements here
      logoutSelectors: [
        'a[href*="destroy_user_session"]',
        'a[href*="/conta/sair"]',
        'a[data-js-event="logout"]',
        'form[action*="destroy_user_session"]',
        'form[action*="/conta/sair"]',
        '[data-logout="true"]',
        '[data-intercom-shutdown="true"]',
        ...(config.logoutSelectors || [])
      ],
      // Auto-show selectors - add any help/support elements here
      showSelectors: [
        '[data-intercom-show="true"]',
        '.intercom-show',
        '#js-intercom',
        ...(config.showSelectors || [])
      ],
      // Debug mode
      debug:
        config.debug !== undefined
          ? config.debug
          : window.RAILS_ENV === 'development',
      // App name for logging
      appName: config.appName || 'App'
    };

    this.init();
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    this.log('Initializing Intercom Manager...');
    this.attachEventListeners();
    this.makeGloballyAvailable();
    this.isInitialized = true;
    this.log('Intercom Manager initialized successfully');
  }

  // Core Intercom operations
  show() {
    if (!window.Intercom) {
      this.log('Intercom not available');
      return false;
    }

    this.log('Showing Intercom');
    window.Intercom('show');
    return true;
  }

  shutdown() {
    if (!window.Intercom) {
      this.log('Intercom not available for shutdown');
      return false;
    }

    this.log('Shutting down Intercom');
    window.Intercom('shutdown');
    return true;
  }

  hide() {
    if (!window.Intercom) {
      this.log('Intercom not available');
      return false;
    }

    this.log('Hiding Intercom');
    window.Intercom('hide');
    return true;
  }

  update(data = {}) {
    if (!window.Intercom) {
      this.log('Intercom not available for update');
      return false;
    }

    this.log('Updating Intercom with data:', data);
    window.Intercom('update', data);
    return true;
  }

  boot(data = {}) {
    if (!window.Intercom) {
      this.log('Intercom not available for boot');
      return false;
    }

    this.log('Booting Intercom with data:', data);
    window.Intercom('boot', data);
    return true;
  }

  // Event listener management
  attachEventListeners() {
    this.attachLogoutListeners();
    this.attachShowListeners();
    this.observeDOMChanges();
  }

  attachLogoutListeners() {
    this.config.logoutSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      this.log(
        `Looking for logout selector: ${selector}, found ${elements.length} elements`
      );

      elements.forEach(element => {
        // Avoid duplicate listeners
        if (element.dataset.intercomLogoutBound) {
          return;
        }

        const eventType = element.tagName === 'FORM' ? 'submit' : 'click';
        element.addEventListener(eventType, () => {
          this.shutdown();
        });

        element.dataset.intercomLogoutBound = 'true';
        this.log(`Attached logout listener to: ${selector}`);
      });
    });
  }

  attachShowListeners() {
    this.config.showSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      this.log(
        `Looking for show selector: ${selector}, found ${elements.length} elements`
      );

      elements.forEach(element => {
        // Avoid duplicate listeners
        if (element.dataset.intercomShowBound) {
          this.log(`Element already bound: ${selector}`);
          return;
        }

        element.addEventListener('click', e => {
          e.preventDefault();
          this.log(`Show listener triggered for: ${selector}`);
          this.show();
        });

        element.dataset.intercomShowBound = 'true';
        this.log(
          `Attached show listener to: ${selector} (element: ${element.tagName}#${element.id})`
        );
      });
    });
  }

  observeDOMChanges() {
    // Watch for dynamically added elements
    const observer = new MutationObserver(() => {
      this.attachLogoutListeners();
      this.attachShowListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.log('DOM observer attached');
  }

  // Make functions globally available
  makeGloballyAvailable() {
    // Store manager instance
    const instanceKey = `IntercomManager_${this.config.appName}`;
    window[instanceKey] = this;

    // Create global functions if they don't exist
    if (!window.showIntercom) {
      window.showIntercom = () => this.show();
      window.shutdownIntercom = () => this.shutdown();
      window.hideIntercom = () => this.hide();
      window.updateIntercom = data => this.update(data);
      window.bootIntercom = data => this.boot(data);

      // Debug functions
      window.debugIntercom = () => this.debugStatus();
      window.refreshIntercom = () => this.refresh();
    }

    this.log(
      'Global functions available: showIntercom, shutdownIntercom, hideIntercom, updateIntercom, bootIntercom, debugIntercom, refreshIntercom'
    );
  }

  // Configuration methods
  addLogoutSelector(selector) {
    if (!this.config.logoutSelectors.includes(selector)) {
      this.config.logoutSelectors.push(selector);
      this.attachLogoutListeners();
      this.log(`Added logout selector: ${selector}`);
    }
  }

  addShowSelector(selector) {
    if (!this.config.showSelectors.includes(selector)) {
      this.config.showSelectors.push(selector);
      this.attachShowListeners();
      this.log(`Added show selector: ${selector}`);
    }
  }

  // Utility methods
  log(message, data = null) {
    if (!this.config.debug) {
      return;
    }

    const prefix = `[${this.config.appName} IntercomManager]`;

    if (data) {
      // eslint-disable-next-line no-console
      console.log(`${prefix} ${message}`, data);
    } else {
      // eslint-disable-next-line no-console
      console.log(`${prefix} ${message}`);
    }
  }

  // Manual refresh - call this after dynamic content updates
  refresh() {
    this.log('Refreshing listeners...');
    this.attachEventListeners();
  }

  // Debug method to check current state
  debugStatus() {
    this.log('=== INTERCOM MANAGER DEBUG ===');
    this.log(`App: ${this.config.appName}`);
    this.log(`Initialized: ${this.isInitialized}`);
    this.log(`Intercom available: ${!!window.Intercom}`);

    this.config.showSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      this.log(
        `Show selector "${selector}": ${elements.length} elements found`
      );
      elements.forEach((el, index) => {
        this.log(
          `  - Element ${index}: ${el.tagName}#${el.id}.${
            el.className
          } (bound: ${!!el.dataset.intercomShowBound})`
        );
      });
    });

    this.config.logoutSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      this.log(
        `Logout selector "${selector}": ${elements.length} elements found`
      );
    });
    this.log('=== END DEBUG ===');
  }
}

// Factory function to create and initialize
export function createIntercomManager(config = {}) {
  return new IntercomManager(config);
}

// Auto-initialize function
export function initializeIntercom(config = {}) {
  const appName = config.appName || 'Main';
  const instanceKey = `IntercomManagerInstance_${appName}`;

  if (window[instanceKey]) {
    return window[instanceKey];
  }

  const manager = new IntercomManager(config);
  window[instanceKey] = manager;

  // Add polling for dynamic content
  if (config.enablePolling !== false) {
    let pollCount = 0;
    const maxPolls = 20;

    const pollForComponents = () => {
      if (pollCount >= maxPolls) {
        return;
      }

      pollCount++;
      const intercomButtons = document.querySelectorAll(
        '#js-intercom, [data-intercom-show="true"]'
      );

      let foundNew = false;
      intercomButtons.forEach(btn => {
        if (!btn.dataset.intercomShowBound) {
          foundNew = true;
        }
      });

      if (foundNew) {
        manager.log(
          'Found new intercom elements during polling, refreshing...'
        );
        manager.refresh();
      }

      setTimeout(pollForComponents, 500);
    };

    setTimeout(pollForComponents, 1000);
  }

  return manager;
}

export default IntercomManager;
